import { TableCellProps, useMediaQuery, useTheme } from '@mui/material';
import React, { Component, createContext, useContext, useEffect, useState } from 'react';

import { CCTableActions } from './cc-table-actions';
import { filterRows, paginateRows, sortRows } from './utils';

const INITIAL_ROWS_PER_PAGE = 5;

type CustomSorter = (a: Row, b: Row) => number;

type CustomFilterType = 'text' | 'checkbox' | 'date' | 'unknown';

export type CustomFilter = {
  type?: CustomFilterType;
  component?: React.Component;
  componentProps?: any;
  func?: (value: string | string[]) => (row: Row) => boolean;
};

export type Filters = {
  [columId: string]: {
    value: string | string[] | null;
    func?: Function;
  };
};

export type Order = 'asc' | 'desc';

export type Column = TableCellProps & {
  id: string;
  label: string;

  /**
   * Custom render
   */
  render?: (props: Row) => any;

  /**
   * Sorter
   *
   * if true => sort by default compareFn
   * if false => disable sorting
   * if function => sort by function
   */
  sorter?: boolean | CustomSorter;

  /**
   * Filters
   *
   * if true => filter by default filterFn
   * if false => disable filtering
   * filter by custom component or custom filter func
   * {
   *    [columnId]: { value: string | any[], func?: custom filter function }
   * }
   */
  filter?: boolean | CustomFilter;

  /**
   * Mobile layout style
   */
  mobileSx?: any;
};

export type Row = {
  id: number;
  [key: string]: any;
};

export type DisplayMode = 'table' | 'card';

type CCTableContextType = {
  /**
   * Table title
   */
  title: string;

  /**
   * Columns
   */
  columns: Column[];

  /**
   * Rows
   */
  rows: Row[];
  filteredRows: Row[];
  sortedRows: Row[];
  paginatedRows: Row[];

  /**
   * Sort
   */
  order: Order;
  orderBy: string | null;

  /**
   * Filters
   */
  filters: Filters;

  /**
   * Selected rows ids
   */
  selected: number[];

  /**
   * Pagination active page
   */
  page: number;

  /**
   * Pagination rows count per page
   */
  rowsPerPage: number;

  /**
   * Bulk actions component and props
   */
  tableActionsComponent: Component;
  tableActionsComponentProps: any;

  /**
   * Update enhanced table context status
   */
  updateTableStatus: (value: Partial<CCTableContextType>) => void;

  /**
   * Display mode
   */
  displayMode?: DisplayMode;
};

const defaultCCTableContextValue: CCTableContextType = {
  title: '',
  columns: [],
  rows: [],
  filteredRows: [],
  sortedRows: [],
  paginatedRows: [],
  order: 'asc',
  orderBy: null,
  filters: {},
  selected: [],
  page: 0,
  rowsPerPage: INITIAL_ROWS_PER_PAGE,
  tableActionsComponent: CCTableActions as any,
  tableActionsComponentProps: [],
  updateTableStatus: () => {},
  displayMode: 'table',
};

export const CCTableContext = createContext<CCTableContextType>(defaultCCTableContextValue);

interface CCTableContextProviderProps {
  children?: any;
  title?: string;
  columns?: Column[];
  rows?: Row[];
  tableActionsComponent?: Component;
  tableActionsComponentProps?: any;
  displayMode?: DisplayMode;
}

export const CCTableContextProvider = ({
  title = '',
  columns = [],
  rows = [],
  tableActionsComponent = CCTableActions as any,
  tableActionsComponentProps = [],
  children = null,
  displayMode: _displayMode,
}: CCTableContextProviderProps) => {
  const [{ order, orderBy, filters, selected, page, rowsPerPage }, setCCTableState] = useState<CCTableContextType>({
    ...defaultCCTableContextValue,
    title,
    columns,
    rows,
    tableActionsComponent,
    tableActionsComponentProps,
  });

  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));

  const displayMode = _displayMode || (down_sm ? 'card' : 'table');

  useEffect(() => {
    if (columns.length > 0 && Object.keys(filters).length === 0) {
      const filtersBasedonColumns = columns.reduce((prev, curr) => {
        const { filter = true } = curr;

        if (filter === false) return prev;

        if (filter === true) return { ...(prev || {}), [curr.id]: { value: null } };

        return { ...(prev || {}), [curr.id]: { ...filter, value: null } };
      }, {});

      updateTableStatus({ filters: filtersBasedonColumns });
    }
  }, [columns]);

  const updateTableStatus = (value: Partial<CCTableContextType>) => {
    setCCTableState((prev) => ({ ...prev, ...value }));
  };

  const filteredRows = filterRows({ rows, filters });

  const sortedRows = sortRows({ rows: filteredRows, columns, orderBy, order });

  const paginatedRows = paginateRows({ rows: sortedRows, page, rowsPerPage });

  return (
    <CCTableContext.Provider
      value={{
        title,
        columns,
        rows,
        filteredRows,
        sortedRows,
        paginatedRows,
        order,
        orderBy,
        filters,
        selected,
        page,
        rowsPerPage,
        tableActionsComponent,
        tableActionsComponentProps,
        displayMode,
        updateTableStatus,
      }}
    >
      {children}
    </CCTableContext.Provider>
  );
};

export const useCCTableContext = () => {
  return useContext(CCTableContext);
};
